import React, { useState } from "react";
import { TruncatedText } from "./truncatedText/TruncatedText";
import { PILL_BASE_STYLE } from "./TrackedChangeOperationTypePill";

export interface EditableCellPillProps {
  label: string;
  onEdit: () => void;
  onRemove: () => void;
  ariaLabelBase: string;
  maxWidth?: number;
  awaitingText?: string;
}

const ICON_BUTTON_STYLE: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  lineHeight: 1,
};

const ICON_IMG_STYLE: React.CSSProperties = {
  width: 12,
  height: 12,
  display: "block",
};

// Absolutely-positioned overlay anchored to the pill's right edge. Rendering the
// action buttons here (instead of inline) keeps the pill's in-flow width constant
// between inactive/active states, so it can never reflow onto a new line when the
// buttons mount on hover/focus (IDS-7140).
const ACTION_OVERLAY_STYLE: React.CSSProperties = {
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  gap: "6px",
  paddingLeft: "12px",
  paddingRight: "8px",
  background: "linear-gradient(to right, transparent, var(--color-blue) 12px)",
};

export const EditableCellPill: React.FC<EditableCellPillProps> = ({
  label,
  onEdit,
  onRemove,
  ariaLabelBase,
  maxWidth,
  awaitingText,
}) => {
  const [isActive, setIsActive] = useState(false);

  const containerStyle: React.CSSProperties = {
    ...PILL_BASE_STYLE,
    backgroundColor: "var(--color-blue)",
    position: "relative",
    ...(maxWidth !== undefined ? { maxWidth } : {}),
  };

  // Keyboard parity for the hover reveal: the pill is focusable so keyboard users
  // can surface the edit/remove actions just like mouse hover does. The container
  // itself stays presentational — only the nested buttons are operable controls.
  const revealAffordanceProps = {
    tabIndex: 0,
    onMouseEnter: () => setIsActive(true),
    onMouseLeave: () => setIsActive(false),
    onFocus: () => setIsActive(true),
    onBlur: () => setIsActive(false),
  };

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onEdit();
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onEdit();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove();
  };

  const handleRemoveKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onRemove();
    }
  };

  return (
    <span data-testid="editable-cell-pill" style={containerStyle} {...revealAffordanceProps}>
      <TruncatedText text={awaitingText ?? label} style={{ color: "white", fontWeight: 600 }} />
      {isActive && (
        <span style={ACTION_OVERLAY_STYLE}>
          <button
            type="button"
            aria-label={`Edit ${ariaLabelBase}`}
            style={ICON_BUTTON_STYLE}
            onClick={handleEditClick}
            onKeyDown={handleEditKeyDown}
          >
            <img src="/assets/edit.svg" alt="" style={{ ...ICON_IMG_STYLE, filter: "brightness(0) invert(1)" }} />
          </button>
          <button
            type="button"
            aria-label={`Remove ${ariaLabelBase}`}
            style={ICON_BUTTON_STYLE}
            onClick={handleRemoveClick}
            onKeyDown={handleRemoveKeyDown}
          >
            <img src="/assets/Remove.svg" alt="" style={ICON_IMG_STYLE} />
          </button>
        </span>
      )}
    </span>
  );
};
